import type { Echo } from './frame';
import type { Transport } from './types';

/** Spec 4.3: a probe without an echo 3 s after sending is resolved as lost. */
export const DEADLINE_MS = 3000;
/** Spec 4.5: over TCP a probe without an echo within 1 s is late. */
export const LATE_MS = 1000;
const ACK_WINDOW = 32;
/** Records more than this far below the oldest unresolved probe are pruned even during an outage. */
// Must stay well above the runner's 10 s no-echo timeout (100 probes/s x 10 s = 1000 < 2000): pruning inside a pending range would skew the count.
const MAX_KEPT = 2000;

/** `stalled`: the page's main thread was blocked while the probe could still be judged (spec 4.3); it is not judged. */
export type Outcome =
    { kind: 'echoed'; rttMs: number } | { kind: 'late'; rttMs?: number } | { kind: 'up' } | { kind: 'down' } | { kind: 'unknown' } | { kind: 'stalled' };

/**
 * `due`: probes past their deadline. `final`: every probe, a failed session's unanswered ones as lost.
 * `drop`: a planned end; probes past their deadline or already echoed, the unanswered rest is dropped.
 */
export type ResolveMode = 'due' | 'final' | 'drop';

export interface ResolvedProbe {
    seq: number;
    sendMono: number;
    transport: Transport;
    outcome: Outcome;
}

/** Jitter samples of two adjacent echoed probes, attributed to the later probe's send time. */
export interface PairSample {
    atMono: number;
    ipdvMs: number;
    upMs: number;
}

interface ProbeRecord {
    sendMono: number;
    clientSendUs: number;
    rttMs?: number;
    serverRecvUs?: number;
    /** The server received it: an echo or a set ack bit said so. */
    serverSeen: boolean;
    /** Some echo's ack window covered it. */
    covered: boolean;
    /** Direction attributed from the receive count, for probes no window ever covered. */
    ranged?: 'up' | 'down';
    /** Its echo was handled after a main-thread stall that began before it: the arrival time is the stall's, not the line's. */
    stalled?: boolean;
    resolved: boolean;
}

/** A main-thread stall `[fromMono, toMono]`: no handler of the page ran in it. */
interface Stall {
    fromMono: number;
    toMono: number;
}

interface Checkpoint {
    serverRecvUs: number;
    /** Seqs below p are below this echo's ack window. */
    p: number;
    /** Received seqs below p. */
    below: number;
}

export function popcount(x: number): number {
    let v = x >>> 0;
    let n = 0;
    while (v) {
        v &= v - 1;
        v >>>= 0;
        n++;
    }
    return n;
}

/**
 * Resolves the probes of one session (spec 4.3). Per seq, the ack window says whether the server
 * received it; below the window, the receive count tells how many of a run of seqs it received.
 */
export class SessionClassifier {
    private readonly probes = new Map<number, ProbeRecord>();
    private nextUnresolved = 0;
    /** Seeded below every seq (none is below 0), so the first echo's range [0, p) is attributed exactly. */
    private lastCheckpoint: Checkpoint = { serverRecvUs: -Infinity, p: 0, below: 0 };
    /** Number of range attributions whose receive count contradicted the per-seq facts (spec 4.3); the facts won. */
    inconsistent = 0;
    private pairs: PairSample[] = [];
    private stalls: Stall[] = [];

    constructor(
        readonly transport: Transport,
        private readonly deadlineMs = DEADLINE_MS,
        private readonly lateMs = LATE_MS
    ) {}

    sent(seq: number, sendMono: number, clientSendUs: number): void {
        this.probes.set(seq, { sendMono, clientSendUs, serverSeen: false, covered: false, resolved: false });
    }

    /** Records a main-thread stall; report it before the echoes handled at its end. */
    stall(fromMono: number, toMono: number): void {
        this.stalls.push({ fromMono, toMono });
    }

    echo(e: Echo, arrivalMono: number): void {
        const rec = this.probes.get(e.seq);
        if (rec) {
            rec.serverSeen = true;
            rec.covered = true;
            if (rec.rttMs === undefined && !rec.resolved) {
                // The ack facts above still hold; only the arrival time is the stall's.
                if (this.overlapsStall(rec.sendMono, arrivalMono)) rec.stalled = true;
                else if (arrivalMono - rec.sendMono <= this.deadlineMs) {
                    rec.rttMs = arrivalMono - rec.sendMono;
                    rec.serverRecvUs = e.serverRecvUs;
                    this.pair(e.seq - 1, e.seq);
                    this.pair(e.seq, e.seq + 1);
                }
            }
        }
        if (this.transport === 'udp') this.applyAcks(e);
    }

    resolve(nowMono: number, mode: ResolveMode = 'due'): { probes: ResolvedProbe[]; pairs: PairSample[] } {
        const out: ResolvedProbe[] = [];
        for (;;) {
            const rec = this.probes.get(this.nextUnresolved);
            if (!rec) break;
            const due = nowMono - rec.sendMono >= this.deadlineMs;
            if (mode === 'due' && !due) break;
            rec.resolved = true;
            if (mode !== 'drop' || due || rec.rttMs !== undefined) {
                out.push({ seq: this.nextUnresolved, sendMono: rec.sendMono, transport: this.transport, outcome: this.classify(rec) });
            }
            this.nextUnresolved++;
        }
        // Keep what a later range attribution or jitter pair can still need.
        const keepFrom = Math.max(Math.min(this.nextUnresolved, this.lastCheckpoint.p) - 1, this.nextUnresolved - MAX_KEPT);
        for (const seq of this.probes.keys()) if (seq < keepFrom) this.probes.delete(seq);
        // A stall can only matter to a probe sent before it ended; every later probe is sent after now.
        const oldest = this.probes.get(this.nextUnresolved)?.sendMono;
        this.stalls = oldest === undefined ? [] : this.stalls.filter((s) => s.toMono > oldest);
        const pairs = this.pairs;
        this.pairs = [];
        return { probes: out, pairs };
    }

    private applyAcks(e: Echo): void {
        const hi = e.ackHi;
        const top = this.probes.get(hi);
        if (top) {
            top.serverSeen = true;
            top.covered = true;
        }
        for (let i = 0; i < ACK_WINDOW; i++) {
            const seq = hi - 1 - i;
            if (seq < 0) break;
            const rec = this.probes.get(seq);
            if (!rec) continue;
            rec.covered = true;
            if ((e.ackBits >>> i) & 1) rec.serverSeen = true;
        }
        const cp: Checkpoint = { serverRecvUs: e.serverRecvUs, p: hi - ACK_WINDOW, below: e.recvCount - 1 - popcount(e.ackBits) };
        const last = this.lastCheckpoint;
        // Echoes can arrive out of order; the server's own clock orders the checkpoints.
        if (cp.serverRecvUs <= last.serverRecvUs) return;
        if (cp.p > last.p) this.attribute(last, cp);
        this.lastCheckpoint = cp;
    }

    /**
     * Between two checkpoints the server received `b.below - a.below` seqs of [a.p, b.p). Those
     * not already known as received and never covered by a window get their direction from that
     * count: the first ones down, the rest up (the count is exact, the per-seq order is not).
     */
    private attribute(a: Checkpoint, b: Checkpoint): void {
        const received = b.below - a.below;
        let known = 0;
        const open: ProbeRecord[] = [];
        for (let seq = Math.max(0, a.p); seq < b.p; seq++) {
            const rec = this.probes.get(seq);
            if (!rec) continue;
            if (rec.rttMs !== undefined || rec.serverSeen) known++;
            else if (!rec.covered) open.push(rec);
        }
        if (received - known < 0 || received - known > open.length) this.inconsistent++;
        const down = Math.min(open.length, Math.max(0, received - known));
        open.forEach((rec, i) => (rec.ranged = i < down ? 'down' : 'up'));
    }

    private pair(a: number, b: number): void {
        const x = this.probes.get(a);
        const y = this.probes.get(b);
        if (!x || !y || x.rttMs === undefined || y.rttMs === undefined || x.serverRecvUs === undefined || y.serverRecvUs === undefined) return;
        const upUs = y.serverRecvUs - x.serverRecvUs - (y.clientSendUs - x.clientSendUs);
        this.pairs.push({ atMono: y.sendMono, ipdvMs: Math.abs(y.rttMs - x.rttMs), upMs: Math.abs(upUs) / 1000 });
    }

    /** Whether `[fromMono, toMono]` overlaps a stall; touching its end does not. */
    private overlapsStall(fromMono: number, toMono: number): boolean {
        return this.stalls.some((s) => fromMono < s.toMono && toMono > s.fromMono);
    }

    private classify(rec: ProbeRecord): Outcome {
        // Without a clean echo, a probe that could still be judged during a stall is not judged at all.
        if (rec.rttMs === undefined && (rec.stalled || this.overlapsStall(rec.sendMono, rec.sendMono + this.deadlineMs))) return { kind: 'stalled' };
        if (this.transport === 'tcp') {
            if (rec.rttMs !== undefined && rec.rttMs <= this.lateMs) return { kind: 'echoed', rttMs: rec.rttMs };
            return rec.rttMs !== undefined ? { kind: 'late', rttMs: rec.rttMs } : { kind: 'late' };
        }
        if (rec.rttMs !== undefined) return { kind: 'echoed', rttMs: rec.rttMs };
        if (rec.serverSeen) return { kind: 'down' };
        if (rec.covered) return { kind: 'up' };
        return rec.ranged ? { kind: rec.ranged } : { kind: 'unknown' };
    }
}
